import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RocketIcon, AlertTriangleIcon, SparklesIcon } from 'lucide-react';
interface OnboardingProps {
  onComplete: () => void;
}
export function Onboarding({
  onComplete
}: OnboardingProps) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [isReturning, setIsReturning] = useState(false);
  const [showZoomOut, setShowZoomOut] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  useEffect(() => {
    const storedName = localStorage.getItem('periclesUserName');
    if (storedName) {
      setName(storedName);
      setIsReturning(true);
    }
  }, []);
  const handleNameSubmit = () => {
    if (name.trim()) {
      localStorage.setItem('periclesUserName', name.trim());
      triggerTransition(() => setStep(1));
    }
  };
  const triggerTransition = (callback: () => void) => {
    setShowTransition(true);
    setTimeout(() => {
      callback();
      setShowTransition(false);
    }, 800);
  };
  const handleComplete = () => {
    setShowZoomOut(true);
    setTimeout(() => {
      localStorage.setItem('periclesOnboardingComplete', 'true');
      onComplete();
    }, 2000);
  };
  const steps = [
  // Step 0: Name input
  {
    content: <motion.div key="name" initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 1.2
    }} transition={{
      duration: 0.6
    }} className="text-center space-y-6 md:space-y-8 px-4" data-id="element-163">
          <motion.div animate={{
        scale: [1, 1.2, 1]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-164">
            <SparklesIcon className="w-16 h-16 md:w-24 md:h-24 mx-auto text-[#60A5FA]" data-id="element-165" />
          </motion.div>
          {isReturning ? <>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white px-4" data-id="element-166">
                Welcome Back, {name}!
              </h1>
              <p className="text-lg md:text-2xl text-slate px-4" data-id="element-167">
                Ready to dive back into the markets?
              </p>
              <button onClick={() => triggerTransition(() => setStep(1))} className="px-8 md:px-12 py-3 md:py-4 bg-[#60A5FA] text-white rounded-lg font-semibold text-lg md:text-xl hover:bg-[#3B82F6] transition-all duration-300 transform hover:scale-105" data-id="element-168">
                Continue
              </button>
            </> : <>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white px-4" data-id="element-169">
                Welcome to Pericles Partners
              </h1>
              <p className="text-lg md:text-2xl text-slate px-4" data-id="element-170">
                What should we call you?
              </p>
              <div className="max-w-md mx-auto px-4" data-id="element-171">
                <input type="text" value={name} onChange={e => setName(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleNameSubmit()} placeholder="Enter your name" className="w-full px-4 md:px-6 py-3 md:py-4 bg-navy-800 border-2 border-navy-700 rounded-lg text-lg md:text-xl text-white placeholder-slate focus:outline-none focus:border-[#60A5FA] transition-colors text-center" autoFocus data-id="element-172" />
                <button onClick={handleNameSubmit} disabled={!name.trim()} className="mt-4 md:mt-6 w-full px-8 md:px-12 py-3 md:py-4 bg-[#60A5FA] text-white rounded-lg font-semibold text-lg md:text-xl hover:bg-[#3B82F6] transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" data-id="element-173">
                  Next
                </button>
              </div>
            </>}
        </motion.div>
  },
  // Step 1: Introduction
  {
    content: <motion.div key="intro" initial={{
      opacity: 0,
      y: 50
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -50
    }} transition={{
      duration: 0.6
    }} className="text-center space-y-6 md:space-y-8 max-w-3xl mx-auto px-4" data-id="element-174">
          <motion.div animate={{
        y: [0, -20, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-175">
            <RocketIcon className="w-16 h-16 md:w-24 md:h-24 mx-auto text-[#14B8A6]" data-id="element-176" />
          </motion.div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" data-id="element-177">
            Welcome to Pericles Partners
          </h2>
          <div className="space-y-4 md:space-y-6 text-base md:text-lg text-slate" data-id="element-178">
            <p data-id="element-179">
              We're a{' '}
              <span className="text-[#60A5FA] font-semibold" data-id="element-180">
                student-driven quantitative research lab
              </span>{' '}
              exploring the intersection of finance, mathematics, data science,
              and unconventional thinking.
            </p>
            <p data-id="element-181">
              Think of us as a{' '}
              <span className="text-[#14B8A6] font-semibold" data-id="element-182">
                quant fund × venture studio
              </span>{' '}
              — where weird ideas meet real money.
            </p>
            <p data-id="element-183">
              We hunt for alpha in markets too small for big funds and too
              interesting to ignore.
            </p>
          </div>
          <button onClick={() => triggerTransition(() => setStep(2))} className="px-8 md:px-12 py-3 md:py-4 bg-[#14B8A6] text-white rounded-lg font-semibold text-lg md:text-xl hover:bg-[#0D9488] transition-all duration-300 transform hover:scale-105" data-id="element-184">
            Tell Me More
          </button>
        </motion.div>
  },
  // Step 2: Disclaimer
  {
    content: <motion.div key="disclaimer" initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 1.2
    }} transition={{
      duration: 0.6
    }} className="text-center space-y-6 md:space-y-8 max-w-3xl mx-auto px-4" data-id="element-185">
          <motion.div animate={{
        scale: [1, 1.1, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-186">
            <AlertTriangleIcon className="w-16 h-16 md:w-24 md:h-24 mx-auto text-[#F59E0B]" data-id="element-187" />
          </motion.div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" data-id="element-188">
            Important Disclaimer
          </h2>
          <div className="bg-navy-800 border-2 border-[#F59E0B] rounded-lg p-4 md:p-8 space-y-3 md:space-y-4 text-left" data-id="element-189">
            <p className="text-sm md:text-base text-slate" data-id="element-190">
              <span className="text-[#F59E0B] font-semibold" data-id="element-191">
                ⚠️ Educational Purpose Only:
              </span>{' '}
              Pericles Partners is a student research organization focused on
              learning and exploration.
            </p>
            <p className="text-sm md:text-base text-slate" data-id="element-192">
              <span className="text-[#F59E0B] font-semibold" data-id="element-193">
                ⚠️ Not Investment Advice:
              </span>{' '}
              Nothing on this site constitutes investment advice or an offer to
              buy/sell securities.
            </p>
            <p className="text-sm md:text-base text-slate" data-id="element-194">
              <span className="text-[#F59E0B] font-semibold" data-id="element-195">
                ⚠️ No Capital Management:
              </span>{' '}
              We do not manage external capital. This is a pre-professional
              research lab.
            </p>
            <p className="text-sm md:text-base text-slate" data-id="element-196">
              <span className="text-[#F59E0B] font-semibold" data-id="element-197">
                ⚠️ Risk Warning:
              </span>{' '}
              All trading and investment activities involve substantial risk of
              loss.
            </p>
          </div>
          <button onClick={() => triggerTransition(() => setStep(3))} className="px-8 md:px-12 py-3 md:py-4 bg-[#F59E0B] text-white rounded-lg font-semibold text-lg md:text-xl hover:bg-[#D97706] transition-all duration-300 transform hover:scale-105" data-id="element-198">
            I Understand
          </button>
        </motion.div>
  },
  // Step 3: Ready to explore
  {
    content: <motion.div key="ready" initial={{
      opacity: 0,
      scale: 0.5
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 2
    }} transition={{
      duration: 0.6
    }} className="text-center space-y-8 md:space-y-12 relative px-4" data-id="element-199">
          <motion.div animate={{
        scale: [1, 1.3, 1]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-200">
            <div className="w-24 h-24 md:w-32 md:h-32 mx-auto rounded-full bg-gradient-to-r from-[#60A5FA] via-[#14B8A6] to-[#10B981] flex items-center justify-center" data-id="element-201">
              <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-navy-900 flex items-center justify-center" data-id="element-202">
                <SparklesIcon className="w-12 h-12 md:w-16 md:h-16 text-white" data-id="element-203" />
              </div>
            </div>
          </motion.div>
          <div className="space-y-4 md:space-y-6" data-id="element-204">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white" data-id="element-205">
              Ready to Explore, {name}?
            </h2>
            <p className="text-lg md:text-2xl text-slate" data-id="element-206">
              Let's dive into the world of quantitative finance
            </p>
          </div>
          <button onClick={handleComplete} className="px-10 md:px-16 py-4 md:py-6 bg-gradient-to-r from-[#60A5FA] to-[#14B8A6] text-white rounded-lg font-bold text-xl md:text-2xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110" data-id="element-207">
            Let's Go! 🚀
          </button>
        </motion.div>
  }];
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-208">
      {/* Animated space background */}
      <motion.div className="absolute inset-0 bg-navy-900" animate={{
      scale: showZoomOut ? 100 : 1
    }} transition={{
      duration: 2,
      ease: [0.43, 0.13, 0.23, 0.96]
    }} data-id="element-209">
        {/* Stars */}
        <div className="absolute inset-0" data-id="element-210">
          {Array.from({
          length: 100
        }).map((_, i) => <motion.div key={i} className="absolute w-1 h-1 bg-white rounded-full" style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`
        }} animate={{
          opacity: [0.2, 1, 0.2],
          scale: [1, 1.5, 1]
        }} transition={{
          duration: 2 + Math.random() * 3,
          repeat: Infinity,
          delay: Math.random() * 2
        }} data-id="element-211" />)}
        </div>
        {/* Step transition zoom circles */}
        <AnimatePresence data-id="element-212">
          {showTransition && <>
              {Array.from({
            length: 3
          }).map((_, i) => <motion.div key={`transition-${i}`} className="absolute border-4 border-[#60A5FA] rounded-full pointer-events-none" style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80px',
            height: '80px'
          }} initial={{
            scale: 0,
            opacity: 0.6
          }} animate={{
            scale: [0, 20],
            opacity: [0.6, 0]
          }} transition={{
            duration: 0.8,
            delay: i * 0.1,
            ease: 'easeOut'
          }} data-id="element-213" />)}
            </>}
        </AnimatePresence>
        {/* Final zoom out effect */}
        <AnimatePresence data-id="element-214">
          {showZoomOut && <motion.div className="absolute inset-0 bg-white" initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 100,
          opacity: 1
        }} transition={{
          duration: 2,
          ease: [0.43, 0.13, 0.23, 0.96]
        }} data-id="element-215" />}
        </AnimatePresence>
        {/* Final expanding zoom circles */}
        <AnimatePresence data-id="element-216">
          {showZoomOut && <>
              {Array.from({
            length: 5
          }).map((_, i) => <motion.div key={i} className="absolute border-4 border-[#60A5FA] rounded-full pointer-events-none" style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100px',
            height: '100px'
          }} initial={{
            scale: 0,
            opacity: 0.8
          }} animate={{
            scale: [0, 30],
            opacity: [0.8, 0]
          }} transition={{
            duration: 2,
            delay: i * 0.15,
            ease: 'easeOut'
          }} data-id="element-217" />)}
            </>}
        </AnimatePresence>
      </motion.div>
      {/* Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 md:px-6" data-id="element-218">
        <AnimatePresence mode="wait" data-id="element-219">{steps[step].content}</AnimatePresence>
      </div>
      {/* Progress indicator */}
      {!showZoomOut && <div className="absolute bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 md:gap-3" data-id="element-220">
          {steps.map((_, i) => <motion.div key={i} className="w-2 h-2 md:w-3 md:h-3 rounded-full" animate={{
        backgroundColor: i === step ? '#60A5FA' : '#334155',
        scale: i === step ? 1.3 : 1
      }} transition={{
        duration: 0.3
      }} data-id="element-221" />)}
        </div>}
    </div>;
}